import { cn } from "@/lib/utils";

/** The ONLY wording for our status (OpenAI Social Guidance, Oct 2026: "when
 *  referencing your status in copy, use: OpenAI Select Partner"). Never
 *  "official", "exclusive", "certified by OpenAI", "endorsed" or "strategic
 *  partnership", never a tier requirement, never a comparison with another
 *  vendor or partner. Kept in English on every locale. */
export const OPENAI_SELECT_PARTNER = "OpenAI Select Partner";

/** Approved status line, English on every locale. */
export const OPENAI_PARTNER_APPROVED_LINE =
  "Anchor Sprint is an OpenAI Select Partner, part of the OpenAI Partner Network.";

/** Trademark line required wherever the badge or the status appears. */
export const OPENAI_TRADEMARK_LINE =
  "OpenAI, ChatGPT and the OpenAI logo are trademarks of OpenAI. Anchor Sprint is an OpenAI Select Partner; this does not imply endorsement of our services.";

/** Path of the landscape badge, exactly as supplied by OpenAI. */
export const OPENAI_BADGE_SRC = "/images/partners/OpenAI_Select_Partner_Badge.svg";

/**
 * The official OpenAI Select Partner badge, rendered unmodified.
 * Rules (OpenAI Social Guidance + Jazz, 2026-10-08), all binding:
 *  - Use the badge as provided: no recolour, crop, filter, border or shadow on
 *    the artwork, and no other OpenAI logo anywhere on the site.
 *  - Only on /partners/openai-partner/ (hero), en/ms/zh. (The announcement post
 *    was dropped from scope on 2026-10-08: website content only.)
 *  - NEVER on a page that also renders the Claude Partner Network badge
 *    (ClaudePartnerCertifiedBadge), and never in a row with other logos.
 *    tests/openai-select-partner.spec.ts enforces both.
 *  - Adequate size on a clean background: drawn at a fixed 320px wide (the
 *    supplied artwork is 1125x531) on a plain white panel with >=20px clear
 *    space on every side, including a 360px phone.
 */
export function OpenAISelectPartnerBadge({
  className,
  showLine = true,
}: {
  readonly className?: string;
  readonly showLine?: boolean;
}) {
  return (
    <div className={cn("flex flex-col items-start gap-4", className)}>
      <div className="flex justify-center bg-white p-6 sm:p-8">
        <img
          src={OPENAI_BADGE_SRC}
          alt="OpenAI Select Partner badge"
          width={320}
          height={151}
          className="block h-[151px] w-[320px] max-w-none"
        />
      </div>
      {showLine && (
        // data-i18n-skip: the client-side DOM translator would otherwise turn
        // "Anchor Sprint" into its zh transliteration and break the approved line.
        <p
          data-i18n-skip
          className="max-w-sm text-base font-semibold leading-snug text-neutral-emphasis"
        >
          {OPENAI_PARTNER_APPROVED_LINE}
        </p>
      )}
    </div>
  );
}

/**
 * Text-only credential chip for credential rows (homepage hero), matching the
 * text-only Claude Partner Network chip. Deliberately NOT the badge artwork:
 * the artwork may not sit next to other logos (the hero row carries MDEC and
 * HRD Corp marks) and needs >=300px on a clean panel. Plain text in our type,
 * status exactly "OpenAI Select Partner", English on every locale. The footer
 * carries the OpenAI trademark line on every page that shows this.
 */
export function OpenAIPartnerChip({
  href,
  className,
}: {
  readonly href: string;
  readonly className?: string;
}) {
  return (
    <a
      href={href}
      data-i18n-skip
      className={cn(
        "flex min-h-11 min-w-0 items-center gap-2 rounded-xl border border-gray-100/50 bg-white/90 px-3 py-2 shadow-md backdrop-blur-sm transition-all duration-200 hover:scale-[1.01] hover:bg-white hover:shadow-lg sm:inline-flex sm:gap-2.5 sm:px-3.5",
        className,
      )}
    >
      <span className="flex min-w-0 flex-1 flex-col text-left leading-tight">
        <span className="text-[11px] font-semibold text-gray-800 sm:text-xs">
          {OPENAI_SELECT_PARTNER}
        </span>
        <span className="text-[9px] leading-tight text-gray-500 sm:text-[10px]">
          Part of the OpenAI Partner Network
        </span>
      </span>
    </a>
  );
}
